import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import PageHeader from '../../components/common/PageHeader';
import SourceCombobox from '../../components/common/SourceCombobox';
import { MT5AccountUser } from '../../utils/types';
import { mt5API } from '../../services/api';
import LoaderOverlay from '../../components/common/LoaderOverlay';
import { USER_ROUTES } from '../../constants/routeConstants';
import {
  getCommentValidationMessage,
  MAX_COMMENT_LENGTH,
  MAX_TRANSACTION_AMOUNT,
  MIN_COMMENT_LENGTH,
  MIN_TRANSACTION_AMOUNT,
  preventInvalidNumberInput,
  preventInvalidNumberPaste,
} from '../../utils/validationMessages';

const Withdrawal = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const passedLogin =
    location.state?.login ||
    location.state?.loginId ||
    searchParams.get('login') ||
    searchParams.get('loginId') ||
    '';

  const [accounts, setAccounts] = useState<MT5AccountUser[]>([]);
  const [selectedLogin, setSelectedLogin] = useState<string>(
    passedLogin ? String(passedLogin) : ''
  );
  const [amount, setAmount] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errors, setErrors] = useState<{
    login?: string;
    amount?: string;
    comment?: string;
  }>({});

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (passedLogin) {
      setSelectedLogin(String(passedLogin));
    }
  }, [passedLogin]);

  const fetchAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const response = await mt5API.getMyAccounts();
      const data = response?.data || response;
      if (Array.isArray(data) && data.length > 0) {
        const mapped = data.map((acc: any) => ({
          Login: acc.mt5Login ?? acc.Login,
          Name: acc.name ?? acc.Name,
          Balance: acc.balance ?? acc.Balance ?? 0,
          Group: acc.group ?? acc.Group,
          Leverage: acc.leverage ?? acc.Leverage,
          Credit: acc.credit ?? acc.Credit ?? 0,
          Email: acc.email ?? acc.Email,
          AccountType: acc.accountType ?? acc.AccountType,
          Country: acc.country ?? acc.Country ?? null,
          Status: acc.status ?? acc.Status ?? null,
        }));
        setAccounts(mapped);
        if (passedLogin) {
          setSelectedLogin(String(passedLogin));
        }
      }
    } catch (error) {
      console.error('Error fetching accounts for withdrawal:', error);
      toast.error('Failed to load trading accounts.');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const selectedAccountObj = accounts.find(
    acc => String(acc.Login) === String(selectedLogin)
  );

  const numAmount = Number(amount);
  const maxBalance = selectedAccountObj ? selectedAccountObj.Balance || 0 : 0;
  const trimmedCommentLength = comment.trim().length;

  const isFormValid =
    Boolean(selectedLogin) &&
    Boolean(amount) &&
    !isNaN(numAmount) &&
    numAmount >= MIN_TRANSACTION_AMOUNT &&
    numAmount <= MAX_TRANSACTION_AMOUNT &&
    Boolean(selectedAccountObj) &&
    numAmount <= maxBalance &&
    trimmedCommentLength >= MIN_COMMENT_LENGTH &&
    trimmedCommentLength <= MAX_COMMENT_LENGTH;

  const validate = () => {
    const newErrors: {
      login?: string;
      amount?: string;
      comment?: string;
    } = {};

    if (!selectedLogin) {
      newErrors.login = 'Please select a trading account.';
    }

    const numericAmount = Number(amount);
    const commentError = getCommentValidationMessage(comment);

    if (!amount || isNaN(numericAmount)) {
      newErrors.amount = `Please enter a valid withdrawal amount between $${MIN_TRANSACTION_AMOUNT} and $${MAX_TRANSACTION_AMOUNT}.`;
    } else if (numericAmount < MIN_TRANSACTION_AMOUNT) {
      newErrors.amount = `Withdrawal amount must be at least $${MIN_TRANSACTION_AMOUNT}.`;
    } else if (numericAmount > MAX_TRANSACTION_AMOUNT) {
      newErrors.amount = `Withdrawal amount cannot exceed $${MAX_TRANSACTION_AMOUNT}.`;
    } else if (
      selectedAccountObj &&
      numericAmount > (selectedAccountObj.Balance || 0)
    ) {
      newErrors.amount = `Withdrawal amount cannot exceed account balance ($${(selectedAccountObj.Balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}).`;
    }

    if (commentError) {
      newErrors.comment = commentError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        login: isNaN(Number(selectedLogin))
          ? selectedLogin
          : Number(selectedLogin),
        amount: Number(amount),
        comment: comment.trim() || 'Client withdraw',
      };

      const response = await mt5API.withdraw(payload);
      if (response?.success !== false) {
        toast.success(
          response?.message || 'Withdrawal processed successfully!'
        );
        setAmount('');
        setComment('');
        if (!passedLogin) {
          setSelectedLogin('');
        }
        setErrors({});
        fetchAccounts();
      } else {
        toast.error(response?.message || 'Failed to process withdrawal.');
      }
    } catch (error: any) {
      console.error('Error executing withdrawal:', error);
      toast.error(
        error?.response?.data?.message || 'Error processing withdrawal request.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LoaderOverlay
      isLoading={submitting}
      loadingText="Processing Withdrawal..."
    >
      <div className="space-y-6">
        <div className="sticky top-0 z-20 bg-background pt-4">
          <PageHeader
            title="Withdraw Funds"
            description="Transfer money from your trading wallet back to your account."
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Withdrawal Request</CardTitle>
            </CardHeader>
            <CardContent>
              <LoaderOverlay
                isLoading={loadingAccounts}
                loadingText="Loading trading accounts..."
              >
                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="account">
                        From Trading Account{' '}
                        <span className="text-red-500">*</span>
                      </Label>
                      <SourceCombobox
                        id="account"
                        disabled={!!passedLogin}
                        items={accounts.map(
                          acc => `${acc.Login} (${acc.Name || 'Account'})`
                        )}
                        placeholder="Select trading account"
                        value={
                          selectedAccountObj
                            ? `${selectedAccountObj.Login} (${selectedAccountObj.Name || 'Account'})`
                            : selectedLogin
                              ? String(selectedLogin)
                              : ''
                        }
                        onChange={val => {
                          if (passedLogin) return;
                          const loginNum = val.split(' ')[0];
                          setSelectedLogin(loginNum);
                          const chosenAccount = accounts.find(
                            acc => String(acc.Login) === String(loginNum)
                          );
                          const numAmt = Number(amount);
                          if (!loginNum) {
                            setErrors(prev => ({
                              ...prev,
                              login: 'Please select a trading account.',
                            }));
                          } else {
                            setErrors(prev => ({ ...prev, login: undefined }));
                          }

                          if (amount && chosenAccount) {
                            if (isNaN(numAmt) || numAmt <= 0) {
                              setErrors(prev => ({
                                ...prev,
                                amount:
                                  'Please enter a valid withdrawal amount greater than 0.',
                              }));
                            } else if (numAmt > (chosenAccount.Balance || 0)) {
                              setErrors(prev => ({
                                ...prev,
                                amount: `Withdrawal amount cannot exceed account balance ($${(chosenAccount.Balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}).`,
                              }));
                            } else {
                              setErrors(prev => ({
                                ...prev,
                                amount: undefined,
                              }));
                            }
                          }
                        }}
                        emptyText="No trading accounts found."
                      />
                      {errors.login && (
                        <p className="text-xs text-red-500 font-medium">
                          {errors.login}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="amount">
                        Amount ($) <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="amount"
                        type="number"
                        min={MIN_TRANSACTION_AMOUNT}
                        max={MAX_TRANSACTION_AMOUNT}
                        step="0.01"
                        placeholder="Enter withdrawal amount"
                        value={amount}
                        onKeyDown={preventInvalidNumberInput}
                        onPaste={preventInvalidNumberPaste}
                        onChange={e => {
                          const newAmount = e.target.value;
                          setAmount(newAmount);
                          const numAmt = Number(newAmount);

                          if (!newAmount) {
                            setErrors(prev => ({ ...prev, amount: undefined }));
                          } else if (isNaN(numAmt)) {
                            setErrors(prev => ({
                              ...prev,
                              amount: `Please enter a valid withdrawal amount between $${MIN_TRANSACTION_AMOUNT} and $${MAX_TRANSACTION_AMOUNT}.`,
                            }));
                          } else if (numAmt < MIN_TRANSACTION_AMOUNT) {
                            setErrors(prev => ({
                              ...prev,
                              amount: `Withdrawal amount must be at least $${MIN_TRANSACTION_AMOUNT}.`,
                            }));
                          } else if (numAmt > MAX_TRANSACTION_AMOUNT) {
                            setErrors(prev => ({
                              ...prev,
                              amount: `Withdrawal amount cannot exceed $${MAX_TRANSACTION_AMOUNT}.`,
                            }));
                          } else if (
                            selectedAccountObj &&
                            numAmt > (selectedAccountObj.Balance || 0)
                          ) {
                            setErrors(prev => ({
                              ...prev,
                              amount: `Withdrawal amount cannot exceed account balance ($${(selectedAccountObj.Balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}).`,
                            }));
                          } else {
                            setErrors(prev => ({ ...prev, amount: undefined }));
                          }
                        }}
                      />
                      {errors.amount && (
                        <p className="text-xs text-red-500 font-medium">
                          {errors.amount}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="comment">
                      Comment <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="comment"
                      placeholder="add comment"
                      minLength={MIN_COMMENT_LENGTH}
                      maxLength={MAX_COMMENT_LENGTH}
                      value={comment}
                      onChange={e => {
                        setComment(e.target.value);
                        if (errors.comment)
                          setErrors(prev => ({ ...prev, comment: undefined }));
                      }}
                    />
                    {errors.comment && (
                      <p className="text-xs text-red-500 font-medium">
                        {errors.comment}
                      </p>
                    )}
                  </div>

                  {selectedAccountObj && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 rounded-md bg-muted/40 border text-xs">
                      <div>
                        <span className="text-muted-foreground block">
                          Available Balance:
                        </span>
                        <span className="font-semibold text-sm text-green-600 dark:text-green-400">
                          $
                          {(selectedAccountObj.Balance || 0).toLocaleString(
                            'en-US',
                            { minimumFractionDigits: 2 }
                          )}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">
                          Account Group:
                        </span>
                        <span className="font-medium text-sm">
                          {selectedAccountObj.Group || '-'}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">
                          Leverage:
                        </span>
                        <span className="font-medium text-sm">
                          1:{selectedAccountObj.Leverage || '-'}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 rounded-md border border-dashed p-3 text-xs text-muted-foreground bg-muted/30">
                    <p>
                      Typical processing time:&nbsp;
                      <span className="font-medium text-foreground">
                        Processed directly against your MT5 account balance.
                      </span>
                    </p>
                    <p>
                      Make sure you maintain sufficient balance to cover active
                      trading positions.
                    </p>
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (!passedLogin) {
                          setSelectedLogin('');
                        }
                        setAmount('');
                        setComment('');
                        setErrors({});
                      }}
                      disabled={submitting}
                    >
                      Reset
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(USER_ROUTES.USER_WALLET)}
                      disabled={submitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="destructive"
                      disabled={submitting || !isFormValid}
                    >
                      {submitting ? 'Processing...' : 'Submit Withdrawal'}
                    </Button>
                  </div>
                </form>
              </LoaderOverlay>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Withdrawal Policy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <ul className="space-y-2 list-disc list-inside">
                <li>No internal withdrawal fees.</li>
                <li>
                  Withdrawals are processed directly against trading accounts.
                </li>
                <li>All KYC/AML verification checks must be satisfied.</li>
              </ul>
              <div className="border-t pt-4 space-y-1">
                <p className="font-medium text-foreground text-sm">Important</p>
                <p>
                  Your trading positions and margin may be affected by this
                  withdrawal. Ensure you maintain sufficient margin for open
                  positions.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </LoaderOverlay>
  );
};

export default Withdrawal;
